import React, { useEffect, useState } from 'react';
import { useTypedTranslation } from '@app/hooks';
import { Checkbox } from '@material-ui/core';
import { TreeView, TreeItem } from '@material-ui/lab';
import { FolderOpen, Folder, Domain } from '@material-ui/icons';
import { cloneDeep } from 'lodash';
import { generateID } from '@app/helpers';

interface IProps {
    agentConfiguration: any;
    setSyncAgentConfiguration: Function;
}

interface IOUDisplay {
    DistinguishedName: string;
    Name: string;
    NameId: string;
    IsSelected: boolean;
    Id: string;
    Children?: IOUDisplay[];
}

const DELIMITER = '||comma||';

export const DirSyncOuTree = ({ agentConfiguration, setSyncAgentConfiguration }: IProps) => {
    const { translation } = useTypedTranslation();
    const [expanded, setExpanded] = useState<string[]>([agentConfiguration.DirectoryStructure.Name + 'base']);
    const [checkboxesChanged, setCheckboxesChanged] = useState<number>(0);
    const [ouTree, setOuTree] = useState<IOUDisplay>({ DistinguishedName: '', Name: '', NameId: '', IsSelected: false, Id: '', Children: [] });

    useEffect(() => {
        const randomId = generateID(8);

        setOuTree(getOuTreeForDisplay(agentConfiguration.DirectoryStructure, randomId));
    }, []);

    const unescapeLDAPString = (ldapString: string) => {
        let final = '';

        if (!ldapString) {
            return final;
        }

        for (let i = 0; i < ldapString.length; i++) {
            let strChar = ldapString.charAt(i);
            let strCharNext = null;

            if (i < ldapString.length - 1) {
                strCharNext = ldapString.charAt(i + 1);
            }

            if (strChar === '\\' && strCharNext !== '\\') {
                final += '';
            } else {
                final += strChar;
            }
        }

        return final;
    };

    const getOuTreeForDisplay = (ouTreeArg: IOUDisplay, randomId: string) => {
        if (ouTreeArg.Children === undefined || ouTreeArg.Children.length === 0) {
            // base case no more children
            ouTreeArg.NameId = ouTreeArg.Name + randomId;
        } else {
            ouTreeArg.NameId = ouTreeArg.Name + randomId;
            ouTreeArg.Children = ouTreeArg.Children.map(function (item) {
                return getOuTreeForDisplay(item, generateID(8));
            });
        }

        return ouTreeArg;
    };

    const checkAllChildren = (item: any, checked: boolean) => {
        item.IsSelected = checked;

        if (item.Children?.length) {
            item.Children.forEach((child: any) => {
                child.IsSelected = checked;
                checkAllChildren(child, checked);
            });
        }
    };

    const updateTree = (id: string, checked: boolean, ouTree: IOUDisplay, parentPath: string) => {
        const parentsNames: string[] = parentPath.split(DELIMITER);
        const copyOfTree = cloneDeep(ouTree);
        let items: IOUDisplay[] | undefined = [copyOfTree];

        if (!parentPath) {
            checkAllChildren(copyOfTree, checked);
        } else {
            parentsNames.forEach((parentName, idx) => {
                const isLastParents = parentsNames.length - 1 === idx;
                const parentIndex = items?.findIndex((item) => item.Name === parentName);
                let parent: IOUDisplay | undefined;

                if (parentIndex !== undefined && parentIndex !== -1 && items) {
                    parent = items[parentIndex];
                    items = parent.Children;
                }

                if (isLastParents) {
                    const clickedItemIndex = items?.findIndex((item) => item.Name === id);

                    if (items && clickedItemIndex !== undefined && clickedItemIndex !== -1) {
                        const clickedItem = items[clickedItemIndex];

                        clickedItem.IsSelected = checked;

                        checkAllChildren(clickedItem, checked);
                    }
                }
            });
        }

        return copyOfTree;
    };

    const checkBoxClicked = (name: string) => (event: any, checked: boolean) => {
        const updatedTree = updateTree(name, checked, ouTree, event.target.name);

        setOuTree(updatedTree);
        setCheckboxesChanged(checkboxesChanged + 1);
        setSyncAgentConfiguration({ ...agentConfiguration, DirectoryStructure: updatedTree });
    };

    const findSyncedChild = (treeItems: IOUDisplay): boolean => {
        let hasSyncedChild = false;
        if (treeItems.Children !== undefined) {
            treeItems.Children.some(function iter(a) {
                if (a.IsSelected) {
                    hasSyncedChild = true;
                    return true;
                }
                return Array.isArray(a.Children) && a.Children.some(iter);
            });
        }

        return hasSyncedChild;
    };

    const checkAreAllChildrenChecked = (items: IOUDisplay[]) => {
        let selectedCount = 0;
        let children = 0;

        const iterateChildren = (items: IOUDisplay[]) => {
            items?.forEach((item) => {
                children++;

                if (item.Children?.length) {
                    iterateChildren(item.Children);
                }

                if (item.IsSelected) {
                    selectedCount++;
                }
            });
        };

        iterateChildren(items);

        return selectedCount === children;
    };

    const getTreeItemsFromData = (treeItems: IOUDisplay[] | null | undefined, parentPath: string, noMoreChildren: boolean) => {
        if (noMoreChildren) {
            return <TreeItem nodeId={generateID(8)} children={[]} label={translation('NODE_LIST_NO_CHILDREN')} />;
        }

        return treeItems?.map((treeItemData: IOUDisplay, idx: number) => {
            let children;

            if (treeItemData.Children && treeItemData.Children.length > 0) {
                children = getTreeItemsFromData(treeItemData.Children, `${parentPath}${DELIMITER}${treeItemData.Name}`, false);
            } else {
                children = getTreeItemsFromData(null, '', true);
            }

            return (
                <TreeItem
                    key={treeItemData.NameId + idx}
                    nodeId={treeItemData.NameId + idx}
                    label={getLabel(treeItemData, parentPath)}
                    children={children}
                />
            );
        });
    };

    const stopPropagation = (e: React.SyntheticEvent) => e.stopPropagation();
    const getLabel = (ouTreeArg: IOUDisplay, parentPath?: string) => {
        let hasChildrenThatAreSynced: boolean = false;
        let allChildrenAreSynced: boolean = ouTreeArg.Children?.length === 0;

        if (ouTreeArg.Children && ouTreeArg.Children.length > 0) {
            hasChildrenThatAreSynced = findSyncedChild(ouTreeArg);
            allChildrenAreSynced = checkAreAllChildrenChecked(ouTreeArg.Children);
        }

        const checkedIcon = allChildrenAreSynced ? (
            <div className='ouTree__checkbox ouTree__checkbox-checked' />
        ) : (
            <div className='ouTree__checkbox ouTree__checkbox-part-of-children' />
        );

        return (
            <div style={{ display: 'flex', alignItems: 'center' }}>
                <Checkbox
                    color='primary'
                    id={`checkbox-${ouTreeArg.Name}`}
                    name={parentPath}
                    checked={ouTreeArg.IsSelected}
                    onChange={checkBoxClicked(ouTreeArg.Name)}
                    onClick={stopPropagation}
                    indeterminateIcon={<div className='ouTree__checkbox ouTree__checkbox-indeterminate' />}
                    checkedIcon={checkedIcon}
                    indeterminate={!ouTreeArg.IsSelected && hasChildrenThatAreSynced}
                />
                <div>{unescapeLDAPString(ouTreeArg.Name)}</div>
            </div>
        );
    };

    const getBaseTreeItem = (ouTreeArg: IOUDisplay, checksChanged: number) => {
        return (
            <TreeItem
                id={checksChanged.toString()}
                key={ouTreeArg.Name + 'base'}
                icon={<Domain />}
                nodeId={ouTreeArg.Name + 'base'}
                label={getLabel(ouTreeArg)}
                children={getTreeItemsFromData(ouTreeArg.Children, ouTreeArg.Name, false)}
            />
        );
    };

    const handleToggle = (event: React.ChangeEvent<{}>, nodeIds: string[]) => {
        setExpanded(nodeIds);
    };

    return (
        <>
            <TreeView
                defaultCollapseIcon={<FolderOpen />}
                defaultExpandIcon={<Folder />}
                defaultEndIcon={<></>}
                onNodeToggle={handleToggle}
                expanded={expanded}
            >
                {/* Root tree item, special case*/}
                {getBaseTreeItem(ouTree, checkboxesChanged)}
            </TreeView>
        </>
    );
};
